/**
 * 「加入家庭」向导（L 域 §5.5 · `AC-L5.1`–`L5.4`）—— 三阶段：输码 → 预览 → 结果
 *
 * 依据：L 域 PRD **§5.5.1**（策略**不可跳过**）· **§5.5.2**（预览四栏）·
 * **`AC-L5.1`**（预览只读零落库）· **`AC-L5.4`**（`strategy` 二选一必填）·
 * 契约 `JoinPreviewView` / `JoinResultView` / `JoinStrategy`。
 *
 * ## 🔴 四条判断
 *
 * | # | 判断 | 为什么 |
 * |:--:|---|---|
 * | 1 | **先预览再执行**（两段式，不合并） | `AC-L5.1` 要求预览**只读零落库**；合并成一步会让"点一下就把账并了"成为可能 |
 * | 2 | 🔴 **`willMerge === null` ⇒ 不显示策略选择** | 没有源账本可并时，"合并/舍弃"是**无意义的二选一**；此时仍提交 `merge`（`joinStrategyToSubmit()` 有注释：填 `discard` 会让日志说谎） |
 * | 3 | 🔴 **四栏逐项来自服务端**（数量 / 撞名 / 丢弃 / 警示） | 端侧一个数都不算：搬动行数由服务端 `MovedCounts` 下发，丢弃清单连 `disposition` 三态都下发 |
 * | 4 | **成功后必须让调用方刷新账本列表并切上下文** | 加入后 `X-Ledger-Id` 要换成家庭账本 —— 否则用户"加入了但还是看着自己的旧账本" |
 *
 * ## ⚠️ 内容层单独导出（`JoinLedgerBody`）
 *
 * `Dialog` 走 Portal，`renderToStaticMarkup` 拿不到内容 ⇒ 内容层零 Portal、纯受控，供静态断言。
 */

import { useState } from 'react';
import type { ReactElement } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Alert, Box, Button, Card, CardContent, Divider, Radio, Stack, TextField, Typography } from '@mui/material';
import type { JoinPreviewView, JoinResultView, JoinStrategy } from '@jiaying/shared';
import { joinLedger, previewJoinLedger } from '../api/endpoints.js';
import { ApiError } from '../api/http.js';
import {
  joinPreviewColumn,
  joinStrategyOptions,
  joinStrategyToSubmit,
  movedIsEmpty,
  movedLines,
  needsExplicitStrategy,
} from '../features/collab.js';
import {
  JOIN_CODE_LABEL,
  JOIN_DONE_TITLE,
  JOIN_NO_SOURCE_HINT,
  JOIN_PREVIEW_DISCARD_TITLE,
  JOIN_PREVIEW_MERGE_TITLE,
  JOIN_PREVIEW_RENAME_TITLE,
  JOIN_PREVIEW_TITLE,
  JOIN_PREVIEW_WARNING_TITLE,
  JOIN_SECTION_HINT,
  JOIN_SECTION_TITLE,
  JOIN_STRATEGY_TITLE,
  joinDoneExplain,
} from '../features/collab-labels.js';

export type JoinStage = 'input' | 'preview' | 'done';

export interface JoinLedgerBodyProps {
  stage: JoinStage;
  code: string;
  onCode: (v: string) => void;
  preview: JoinPreviewView | null;
  result: JoinResultView | null;
  strategy: JoinStrategy | null;
  onStrategy: (s: JoinStrategy) => void;
  busy: boolean;
  /** 服务端错误原文（400 码无效 / 409 满员）—— 原样展示 */
  error: string | null;
  /** input → preview */
  onPreview: () => void;
  /** preview → done */
  onJoin: () => void;
  /** preview → input */
  onBack: () => void;
  /** done → 关闭 */
  onClose: () => void;
}

export function JoinLedgerBody(props: JoinLedgerBodyProps): ReactElement {
  const {
    stage,
    code,
    onCode,
    preview,
    result,
    strategy,
    onStrategy,
    busy,
    error,
    onPreview,
    onJoin,
    onBack,
    onClose,
  } = props;

  return (
    <Box data-testid="join-ledger-body">
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
        {stage === 'done' ? JOIN_DONE_TITLE : JOIN_SECTION_TITLE}
      </Typography>

      {stage === 'input' && (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {JOIN_SECTION_HINT}
          </Typography>
          <TextField
            size="small"
            fullWidth
            label={JOIN_CODE_LABEL}
            value={code}
            onChange={(e) => {
              onCode(e.target.value);
            }}
            disabled={busy}
            data-testid="join-code-input"
          />
          {error !== null && (
            <Alert severity="error" sx={{ mt: 1.5 }} data-testid="join-error">
              {error}
            </Alert>
          )}
          <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
            <Button
              variant="contained"
              onClick={onPreview}
              /* 🔴 只判非空（可用性）；码的形态由契约层校验，端侧不抄第二遍 */
              disabled={busy || code.trim().length === 0}
              data-testid="join-preview-btn"
            >
              {busy ? '读取中…' : '查看会发生什么'}
            </Button>
          </Stack>
        </>
      )}

      {stage === 'preview' && preview !== null && <JoinPreviewPanel preview={preview} />}

      {stage === 'preview' && preview !== null && (
        <>
          <Divider sx={{ my: 1.5 }} />
          <JoinStrategyPanel
            willMergeIsNull={preview.willMerge === null}
            strategy={strategy}
            onStrategy={onStrategy}
          />
          {error !== null && (
            <Alert severity="error" sx={{ mt: 1.5 }} data-testid="join-error">
              {error}
            </Alert>
          )}
          <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
            <Button onClick={onBack} disabled={busy} data-testid="join-back-btn">
              返回
            </Button>
            <Button
              variant="contained"
              onClick={onJoin}
              disabled={busy || needsExplicitStrategy(preview.willMerge === null, strategy)}
              data-testid="join-confirm-btn"
            >
              {busy ? '加入中…' : '确认加入'}
            </Button>
          </Stack>
        </>
      )}

      {stage === 'done' && result !== null && (
        <>
          <Alert severity="success" sx={{ mb: 1.5 }} data-testid="join-done">
            {joinDoneExplain(preview?.target.name ?? '家庭账本')}
          </Alert>
          {result.moved !== null && (
            <Box data-testid="join-moved">
              <Typography variant="caption" color="text.secondary">
                {result.strategy === 'discard' ? '已舍弃原账本的' : '已并入的'}
              </Typography>
              {movedIsEmpty(result.moved) ? (
                <Typography variant="body2" data-testid="join-moved-empty">
                  没有需要搬动的内容。
                </Typography>
              ) : (
                <Stack component="ul" sx={{ m: 0, pl: 2.5 }} data-testid="join-moved-list">
                  {movedLines(result.moved).map((l) => (
                    <Typography key={l.key} component="li" variant="body2">
                      {l.label} {l.count}
                    </Typography>
                  ))}
                </Stack>
              )}
            </Box>
          )}
          <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
            <Button variant="contained" onClick={onClose} data-testid="join-done-btn">
              完成
            </Button>
          </Stack>
        </>
      )}
    </Box>
  );
}

/** 四栏（`§5.5.2`）—— 每栏标题 + 内容；空栏**显示"无"**而不是消失（消失 = 用户以为没检查过） */
function JoinPreviewPanel({ preview }: { preview: JoinPreviewView }): ReactElement {
  const col = joinPreviewColumn(preview);
  return (
    <Box data-testid="join-preview">
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
        {JOIN_PREVIEW_TITLE}
      </Typography>

      <Typography variant="caption" color="text.secondary">
        {JOIN_PREVIEW_MERGE_TITLE}
      </Typography>
      {col.noSource ? (
        <Typography variant="body2" data-testid="join-no-source">
          {JOIN_NO_SOURCE_HINT}
        </Typography>
      ) : col.merge.length === 0 ? (
        <Typography variant="body2">没有需要并入的内容。</Typography>
      ) : (
        <Stack component="ul" sx={{ m: 0, pl: 2.5 }} data-testid="join-merge-list">
          {col.merge.map((l) => (
            <Typography key={l.label} component="li" variant="body2">
              {l.label} {l.count}
            </Typography>
          ))}
        </Stack>
      )}

      <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
        {JOIN_PREVIEW_RENAME_TITLE}
      </Typography>
      {col.renames.length === 0 && col.reuses.length === 0 ? (
        <Typography variant="body2">没有撞名。</Typography>
      ) : (
        <Stack spacing={0.25} data-testid="join-rename-list">
          {col.renames.map((r) => (
            <Typography key={`${r.from}:${r.to}`} variant="body2">
              「{r.from}」→「{r.to}」
            </Typography>
          ))}
          {col.reuses.map((r) => (
            <Typography key={r.text} variant="body2">
              {r.text}
            </Typography>
          ))}
        </Stack>
      )}

      <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
        {JOIN_PREVIEW_DISCARD_TITLE}
      </Typography>
      {col.discarded.length === 0 ? (
        <Typography variant="body2">没有不并入的内容。</Typography>
      ) : (
        <Stack spacing={0.5} data-testid="join-discard-list">
          {col.discarded.map((d) => (
            <Box key={d.name}>
              <Typography variant="body2">
                {d.name} {d.count} 项 · {d.disposition}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {d.note}
              </Typography>
            </Box>
          ))}
        </Stack>
      )}

      <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
        {JOIN_PREVIEW_WARNING_TITLE}
      </Typography>
      <Alert severity="warning" data-testid="join-warning">
        {col.warning}
      </Alert>
    </Box>
  );
}

/** 策略二选一（🔴 无源时**整块不渲染**） */
function JoinStrategyPanel({
  willMergeIsNull,
  strategy,
  onStrategy,
}: {
  willMergeIsNull: boolean;
  strategy: JoinStrategy | null;
  onStrategy: (s: JoinStrategy) => void;
}): ReactElement | null {
  const options = joinStrategyOptions(willMergeIsNull);
  if (options.length === 0) return null;

  return (
    <Box data-testid="join-strategy">
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
        {JOIN_STRATEGY_TITLE}
      </Typography>
      <Stack spacing={0.5}>
        {options.map((o) => (
          <Box key={o.value}>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Radio
                size="small"
                checked={strategy === o.value}
                onChange={() => {
                  onStrategy(o.value);
                }}
                data-testid={`join-strategy-${o.value}`}
              />
              <Typography variant="body2">{o.label}</Typography>
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ pl: 4 }}>
              {o.hint}
            </Typography>
          </Box>
        ))}
      </Stack>
      {/* 🔴 让"没显式选过就不能提交"这条规则在渲染产物里可断言 */}
      {needsExplicitStrategy(willMergeIsNull, strategy) && (
        <Typography variant="caption" color="text.secondary" component="div" data-testid="join-need-strategy">
          请先选一种处理方式。
        </Typography>
      )}
    </Box>
  );
}

/**
 * 宿主组件：内部管三阶段状态 + 两个 mutation，对外只暴露「打开/关闭」。
 *
 * 🔴 成功后**必须**刷新两处：`['ledgers']`（账本切换器）与 `['ledgerMembers']`（成员名单），
 * 并让调用方拿到 `result.ledgerId` 去 `setLedger` —— 否则"加入了却还看着旧账本"。
 */
export interface JoinLedgerCardProps {
  /** 加入成功后回调（宿主负责 `setLedger` + 失效缓存） */
  onJoined: (result: JoinResultView) => void;
}

export function JoinLedgerCard({ onJoined }: JoinLedgerCardProps): ReactElement {
  const [code, setCode] = useState('');
  const [stage, setStage] = useState<JoinStage>('input');
  const [preview, setPreview] = useState<JoinPreviewView | null>(null);
  const [result, setResult] = useState<JoinResultView | null>(null);
  const [strategy, setStrategy] = useState<JoinStrategy | null>(null);

  const previewMut = useMutation({
    mutationFn: (c: string) => previewJoinLedger(c),
    onSuccess: (data) => {
      setPreview(data);
      setStrategy(null);
      setStage('preview');
    },
  });

  const joinMut = useMutation({
    mutationFn: (args: { code: string; strategy: JoinStrategy }) =>
      joinLedger(args.code, args.strategy),
    onSuccess: (data) => {
      setResult(data);
      setStage('done');
      onJoined(data);
    },
  });

  const err = previewMut.error ?? joinMut.error;

  return (
    <Card data-testid="join-ledger-card">
      <CardContent>
        <JoinLedgerBody
          stage={stage}
          code={code}
          onCode={setCode}
          preview={preview}
          result={result}
          strategy={strategy}
          onStrategy={setStrategy}
          busy={previewMut.isPending || joinMut.isPending}
          error={err === null || err === undefined ? null : err instanceof ApiError ? err.message : String(err)}
          onPreview={() => {
            previewMut.mutate(code.trim());
          }}
          onJoin={() => {
            const willMergeIsNull = preview?.willMerge === null;
            joinMut.mutate({
              code: code.trim(),
              strategy: joinStrategyToSubmit(strategy, willMergeIsNull),
            });
          }}
          onBack={() => {
            setStage('input');
            setPreview(null);
            previewMut.reset();
          }}
          onClose={() => {
            setStage('input');
            setCode('');
            setPreview(null);
            setResult(null);
            setStrategy(null);
            previewMut.reset();
            joinMut.reset();
          }}
        />
      </CardContent>
    </Card>
  );
}
